import { CloseOutlined } from '@ant-design/icons';
import { Checkbox, Col, Modal, Row } from 'antd';
import Title from 'antd/es/typography/Title';
import { reportApis } from 'Api/reports';
import Buttons from 'components/Global/Buttons';
import GlobalPayPeriod from 'components/Global/PayPeriod';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { invalidText, toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
import { getAccessibleBranchForUser } from 'Api/masters/branch-code';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { fetchConstantDropdownAction } from '../../../../redux/actions/constantDropdownAction';
import SelectDropdown from 'components/Global/SelectDropdown';

type GeneratePayrollSummaryModalProps = {
	open: boolean;
	cancelGenerateModal: () => void;
	fetchNetpayReports: () => void;
};
interface DropdownOption {
	label: string;
	value: string;
}
const GenerateNetpayModal = (props: GeneratePayrollSummaryModalProps) => {
	const { open, cancelGenerateModal, fetchNetpayReports } = props;
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch<AppDispatch>();

	const [payPeriodError, setPayPeriodError] = useState<any>(false);

	const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>('');
	const [selectedYear, setSelectedYear] = useState<any>(dayjs());
	const [currencyType, setCurrencyType] = useState<string>('ZMW');
	const [branchIds, setBranchIds] = useState<string[]>([]);
	const [departmentIds, setDepartmentIds] = useState<string[]>([]);
	const [accessibleBranches, setAccessibleBranches] = useState<
		DropdownOption[]
	>([]);
	const [isBranchesLoading, setIsBranchesLoading] = useState<boolean>(false);

	const dropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data
	);

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			disabled: false,
			onclick: () => {
				handleCancelModal();
			},
			minWidth: '10rem',
		},
		{
			text: 'Generate',
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: false,
			onclick: handleSubmit,
			minWidth: '10rem',
		},
	];

	const handleCancelModal = () => {
		setSelectedPayPeriod('');
		setSelectedYear(dayjs());
		setPayPeriodError(false);
		setCurrencyType('');
		cancelGenerateModal();
	};

	async function handleSubmit() {
		if (invalidText(selectedPayPeriod)) {
			setPayPeriodError(true);
			return;
		}
		if (!currencyType) {
			toastText('Please select a currency type', 'error');
			return;
		}

		try {
			setLoading(true);
			const data = {
				payPeriodId: selectedPayPeriod,
				branchIds: branchIds,
				departmentIds: departmentIds,
				currencyType,
			};

			await reportApis.generateNetPayReport(data);

			toastText('Net Pay report generated Successfully', 'success');
			fetchNetpayReports();
			handleCancelModal();
		} catch (error: any) {
			const errorMessage =
				error?.response?.data?.message ||
				'Something went wrong while generating Net pay report';
			toastText(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	}

	const handleSelectYear = (value: any) => {
		setSelectedPayPeriod('');
		setSelectedYear(value);
	};

	const handlePayPeriodChange = (value: string) => {
		if (invalidText(value)) {
			setPayPeriodError(true);
			setSelectedPayPeriod(value);
			return;
		}

		setSelectedPayPeriod(value);
		setPayPeriodError(false);
	};

	useEffect(() => {
		if (open) {
			setSelectedPayPeriod('');
			setSelectedYear(dayjs());
		}
	}, [open]);

	const fetchAccessibleBranches = async () => {
		try {
			setIsBranchesLoading(true);

			const response = await getAccessibleBranchForUser();

			const branches: DropdownOption[] = response?.data?.data ?? [];
			branches.shift();

			setAccessibleBranches(branches);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Failed to fetch accessible branches';
			toastText(message, 'error');
		} finally {
			setIsBranchesLoading(false);
		}
	};

	useEffect(() => {
		fetchAccessibleBranches();
		dispatch(fetchConstantDropdownAction({}));
	}, []);

	return (
		<Modal
			open={open}
			width={500}
			maskClosable={false}
			closable={false}
			onCancel={handleCancelModal}
			footer={null}
			style={{ position: 'absolute', top: 80, right: 50 }}
		>
			<div className={`${styles['modal']} modal`}>
				<div className={styles['modal-header']}>
					<Title level={4}>Net Pay</Title>
					<div
						className={styles['close-icon']}
						onClick={handleCancelModal}
					>
						<CloseOutlined />
					</div>
				</div>
				<div>
					<Row className={styles['form-container-card']} gutter={50}>
						<GlobalPayPeriod
							handlePayPeriod={(value) =>
								handlePayPeriodChange(value)
							}
							selectedPayPeriod={selectedPayPeriod}
							isRow={true}
							handleSelectedYear={(value) =>
								handleSelectYear(value)
							}
							isYearRequired={true}
							isPayPeriodRequired={true}
							colSpan={24}
							colStyle={{ marginBottom: '1rem' }}
							payPeriodError={payPeriodError}
							selectedYear={selectedYear}
							rowStyle={{ margin: '0' }}
						/>
						<Col span={24} className="margin-top-10">
							<SelectDropdown
								defaultOption={true}
								defaultLabel="Select Branch"
								placeholder="Select Branch"
								mode="multiple"
								options={accessibleBranches}
								value={branchIds}
								onChange={(value) => {
									const newBranchIds = value as string[];
									setBranchIds(newBranchIds);

									const validDepartments =
										dropdownOptions?.department
											?.filter((item: any) =>
												newBranchIds.includes(
													item.branchId
												)
											)
											.map((item: any) => item.value) ||
										[];

									setDepartmentIds((prev) =>
										prev.filter((id) =>
											validDepartments.includes(id)
										)
									);
								}}
								size="large"
								required={false}
								helperText="Branch is required"
								label="Branch"
								isError={false}
								loading={isBranchesLoading}
								disabled={isBranchesLoading}
							/>
						</Col>
						<Col span={24} className="margin-top-10">
							<SelectDropdown
								defaultOption={true}
								defaultLabel="Select Department"
								placeholder="Select Department"
								mode="multiple"
								options={
									branchIds
										? dropdownOptions?.department?.filter(
												(item: any) =>
													branchIds.includes(
														item.branchId
													)
										  )
										: []
								}
								value={departmentIds}
								onChange={(value) => {
									setDepartmentIds(value as string[]);
								}}
								size="large"
								required={false}
								helperText="Department is required"
								label="Department"
								disabled={branchIds.length === 0}
								isError={false}
							/>
						</Col>
					</Row>
					<p className={styles['title']}>Select Currency Type</p>
					<Row className={styles['currency-selector']}>
						<Col>
							<Row>
								<Checkbox
									checked={currencyType === 'ZMW'}
									onChange={() => setCurrencyType('ZMW')}
								>
									ZMW
								</Checkbox>
							</Row>
							<Row>
								<Checkbox
									className={styles['currency-checkbox']}
									checked={currencyType === 'USD'}
									onChange={() => setCurrencyType('USD')}
								>
									USD
								</Checkbox>
							</Row>
						</Col>
					</Row>
					<Row className={styles['modal-buttons']}>
						<Buttons buttons={buttons} />
					</Row>
				</div>
			</div>
		</Modal>
	);
};

export default GenerateNetpayModal;
